// src/hooks/feature/useMemoryVerses.js
// ─────────────────────────────────────────────────────────────────────────────
//  Storage + actions for the Memory Verse system.
//    verses   → synced (progress follows the user across devices)
//    settings → IDB-only (reminder time / popup state are per-device)
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useMemo } from 'react';
import { useSyncStore } from '@/hooks/sync/useSyncStore';
import { STORE_MEMORY_VERSES, STORE_MEMORY_VERSE_SETTINGS, STORE_MEMORY_VERSE_DAYS } from '@/lib/storeKeys';
import {
  dayKey, newVerse, applyResult, dueVerses, cleanText,
} from '@/lib/scriptureMemory';

export const DEFAULT_MEMORY_SETTINGS = {
  reminderEnabled: false,
  reminderHour:    8,
  reminderMinute:  0,
  popupEnabled:    true,
  lastPopupDate:   '',
  introSeen:       false,
};

export function useMemoryVerses() {
  const [rawVerses,  setVerses,   versesReady]   = useSyncStore(STORE_MEMORY_VERSES, []);
  const [rawSettings, setSettings, settingsReady] = useSyncStore(STORE_MEMORY_VERSE_SETTINGS, {});
  const [, setDays, daysReady] = useSyncStore(STORE_MEMORY_VERSE_DAYS, []);

  const ready    = versesReady && settingsReady && daysReady;
  const verses   = useMemo(() => (Array.isArray(rawVerses) ? rawVerses : []), [rawVerses]);
  const settings = useMemo(() => ({ ...DEFAULT_MEMORY_SETTINGS, ...(rawSettings || {}) }), [rawSettings]);

  const today = dayKey();
  const due   = useMemo(() => dueVerses(verses, today), [verses, today]);

  /** Returns the new verse id, or null when that reference is already saved / input is empty. */
  const addVerse = useCallback((reference, text, translation = '') => {
    const ref = cleanText(reference);
    const body = cleanText(text);
    if (!ref || !body) return null;
    if (verses.some(v => v.reference.toLowerCase() === ref.toLowerCase())) return null;
    const verse = newVerse({ reference: ref, text: body, translation });
    setVerses(prev => [...(Array.isArray(prev) ? prev : []), verse]);
    return verse.id;
  }, [verses, setVerses]);

  const removeVerse = useCallback((id) => {
    setVerses(prev => (Array.isArray(prev) ? prev : []).filter(v => v.id !== id));
  }, [setVerses]);

  /** Records a recall attempt and returns the updated verse (for the result message). */
  const recordResult = useCallback((id, passed) => {
    const current = verses.find(v => v.id === id);
    if (!current) return null;
    const updated = applyResult(current, passed, dayKey());
    setDays(prev => {
      const list = Array.isArray(prev) ? prev : [];
      const day = dayKey();
      return list.includes(day) ? list : [...list, day];
    });
    setVerses(prev => (Array.isArray(prev) ? prev : []).map(v => (v.id === id ? updated : v)));
    return updated;
  }, [verses, setVerses]);

  const updateSettings = useCallback((patch) => {
    setSettings(prev => ({ ...DEFAULT_MEMORY_SETTINGS, ...(prev || {}), ...patch }));
  }, [setSettings]);

  return {
    ready, verses, settings, today,
    dueVerses: due,
    todaysVerse: due[0] || null,
    dueCount: due.length,
    addVerse, removeVerse, recordResult, updateSettings,
  };
}